// src/firebase/settings.js
// Settings Service - manages marking settings

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from './config';
import { getCurrentUserOrgId } from './orgs';

// Every setting below lives in a single per-org doc, orgs/{orgId}/settings/marking.
// Each function accepts an optional trailing orgId param that overrides the
// auto-resolved "current signed-in user's org" - pass it explicitly only
// when the org context is already known and may not match the signed-in
// user's own profile (e.g. registration.js, mid-signup).
// Every get* below falls back to a default on a failed read, so screens that
// only display settings keep working. A screen that saves settings back (Head
// Marker Settings > Save) must not save those fallbacks over the real values,
// so each failed read is counted here for it to check.
let settingsReadFailures = 0;
export const getSettingsReadFailureCount = () => settingsReadFailures;

const getMarkingSettingsRef = async (orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  return doc(db, 'orgs', resolvedOrgId, 'settings', 'marking');
};

/**
 * Sort homework type codes in a stable, predictable order: TH-prefixed
 * numerically, then RH-prefixed numerically, then anything else
 * alphabetically (e.g. an org using entirely different codes).
 */
// The org's own chosen homework order (Settings > Homework, move up/down),
// stored as homeworkOrder in its marking settings. Cached here whenever the
// settings doc is read by getHomeworkNames - every screen loads homework
// names before sorting them - so sortHomeworkTypes can stay synchronous
// and every existing caller follows the tutor's order with no changes.
let cachedHomeworkOrder = null;

export const sortHomeworkTypes = (types) => {
  if (Array.isArray(cachedHomeworkOrder) && cachedHomeworkOrder.length) {
    const position = new Map(cachedHomeworkOrder.map((t, i) => [t, i]));
    // Types the tutor has ordered come first in that order; anything newer
    // than the saved order falls in after them, by the default rule below
    const ordered = types.filter(t => position.has(t)).sort((a, b) => position.get(a) - position.get(b));
    const rest = defaultSortHomeworkTypes(types.filter(t => !position.has(t)));
    return [...ordered, ...rest];
  }
  return defaultSortHomeworkTypes(types);
};

/**
 * Save the org's homework order (full list of homework type codes, first to
 * last) - used everywhere homework types are listed, including the
 * student's homework dropdown.
 */
export const updateHomeworkOrder = async (order, orgId) => {
  const docRef = await getMarkingSettingsRef(orgId);
  await setDoc(docRef, { homeworkOrder: order }, { merge: true });
  cachedHomeworkOrder = [...order];
};

// Whether this org has chosen its own homework order
export const hasCustomHomeworkOrder = () => Array.isArray(cachedHomeworkOrder) && cachedHomeworkOrder.length > 0;

// Default when a tutor hasn't set an order: TH-prefixed numerically, then
// RH-prefixed numerically, then anything else alphabetically.
const defaultSortHomeworkTypes = (types) => {
  return [...types].sort((a, b) => {
    const thMatch = /^TH(\d+)$/;
    const rhMatch = /^RH(\d+)$/;
    const aIsTH = thMatch.test(a), bIsTH = thMatch.test(b);
    const aIsRH = rhMatch.test(a), bIsRH = rhMatch.test(b);
    if (aIsTH && bIsTH) return parseInt(a.match(thMatch)[1]) - parseInt(b.match(thMatch)[1]);
    if (aIsRH && bIsRH) return parseInt(a.match(rhMatch)[1]) - parseInt(b.match(rhMatch)[1]);
    if (aIsTH && !bIsTH) return -1;
    if (!aIsTH && bIsTH) return 1;
    if (aIsRH && !bIsRH) return -1;
    if (!aIsRH && bIsRH) return 1;
    return a.localeCompare(b);
  });
};

/**
 * Get review settings for homework types
 */
export const getReviewSettings = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // Return default settings if none exist
      return {
        TH1: true,
        TH2: false,
        TH3: true,
        TH4: false
      };
    }

    return docSnap.data().reviewSettings || {};
  } catch (error) {
    console.error('Get review settings error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update review settings for homework types
 */
export const updateReviewSettings = async (settings, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, {
      reviewSettings: settings
    }, { merge: true });
    console.log('Review settings updated');
  } catch (error) {
    console.error('Update review settings error:', error);
    throw error;
  }
};

/**
 * Get model-solution release settings for homework types: whether a
 * homework type's model solution should stay hidden from a student until
 * the cohort deadline has passed, on top of their own submission being
 * marked (`true`/unset - preserves the original behavior), or become
 * visible as soon as their own homework is marked and returned regardless
 * of the deadline (`false`).
 */
export const getSolutionReleaseSettings = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().solutionReleaseSettings || {};
  } catch (error) {
    console.error('Get solution release settings error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update model-solution release settings for homework types
 */
export const updateSolutionReleaseSettings = async (settings, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, {
      solutionReleaseSettings: settings
    }, { merge: true });
    console.log('Solution release settings updated');
  } catch (error) {
    console.error('Update solution release settings error:', error);
    throw error;
  }
};

/**
 * Check if a homework type requires review
 */
export const requiresReview = async (homeworkType, orgId) => {
  try {
    const settings = await getReviewSettings(orgId);
    return settings[homeworkType] === true;
  } catch (error) {
    console.error('Check requires review error:', error);
    return false; // Default to no review on error
  }
};

/**
 * Get custom homework names
 */
export const getHomeworkNames = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // Return default names if none exist
      return {
        TH1: 'Tuition Homework 1',
        TH2: 'Tuition Homework 2',
        TH3: 'Tuition Homework 3',
        TH4: 'Tuition Homework 4',
        RH1: 'Revision Homework 1',
        RH2: 'Revision Homework 2',
        RH3: 'Revision Homework 3',
        RH4: 'Revision Homework 4'
      };
    }

    cachedHomeworkOrder = docSnap.data().homeworkOrder || null;
    return docSnap.data().homeworkNames || {};
  } catch (error) {
    console.error('Get homework names error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update custom homework names
 */
export const updateHomeworkNames = async (names, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    // Use updateDoc (not setDoc+merge) so the entire homeworkNames map
    // is replaced — setDoc+merge preserves deleted keys in Firestore maps.
    await updateDoc(docRef, { homeworkNames: names });
    console.log('Homework names updated');
  } catch (error) {
    console.error('Update homework names error:', error);
    throw error;
  }
};

/**
 * Get marker caps (max submissions per marker per homework type)
 */
export const getMarkerCaps = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // Return default - no caps (null means unlimited)
      return {
        TH1: null,
        TH2: null,
        TH3: null,
        TH4: null,
        RH1: null,
        RH2: null,
        RH3: null,
        RH4: null
      };
    }

    return docSnap.data().markerCaps || {};
  } catch (error) {
    console.error('Get marker caps error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update marker caps
 */
export const updateMarkerCaps = async (caps, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, {
      markerCaps: caps
    }, { merge: true });
    console.log('Marker caps updated');
  } catch (error) {
    console.error('Update marker caps error:', error);
    throw error;
  }
};

/**
 * Get max marks (total marks) for each homework type
 * Item 12: Custom total marks per homework
 */
export const getMaxMarks = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // Return default - all homework types default to 20 marks
      return {
        TH1: 20,
        TH2: 20,
        TH3: 20,
        TH4: 20,
        RH1: 20,
        RH2: 20,
        RH3: 20,
        RH4: 20
      };
    }

    return docSnap.data().maxMarks || {};
  } catch (error) {
    console.error('Get max marks error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update max marks (total marks) for homework types
 * Item 12: Custom total marks per homework
 */
export const updateMaxMarks = async (maxMarks, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, {
      maxMarks: maxMarks
    }, { merge: true });
    console.log('Max marks updated');
  } catch (error) {
    console.error('Update max marks error:', error);
    throw error;
  }
};

/**
 * Get max mark for a specific homework type
 * Returns 20 if not set
 */
export const getMaxMarkForHomework = async (homeworkType, orgId) => {
  try {
    const maxMarks = await getMaxMarks(orgId);
    return maxMarks[homeworkType] || 20;
  } catch (error) {
    console.error('Get max mark for homework error:', error);
    settingsReadFailures++;
    return 20;
  }
};

/**
 * Get default WhatsApp template
 */
const getDefaultWhatsAppTemplates = () => {
  const defaultTemplate = `Hi {{studentName}}, your homework has been marked.

{{sectionBreakdown}}

Total: {{total}}/{{maxMark}}

{{finalComment}}`;

  return {
    TH1: defaultTemplate,
    TH2: defaultTemplate,
    TH3: defaultTemplate,
    TH4: defaultTemplate,
    RH1: defaultTemplate,
    RH2: defaultTemplate,
    RH3: defaultTemplate,
    RH4: defaultTemplate
  };
};

/**
 * Get WhatsApp message templates for each homework type
 * Item 5: Custom WhatsApp templates
 */
export const getWhatsAppTemplates = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return getDefaultWhatsAppTemplates();
    }

    return docSnap.data().whatsappTemplates || getDefaultWhatsAppTemplates();
  } catch (error) {
    console.error('Get WhatsApp templates error:', error);
    settingsReadFailures++;
    return getDefaultWhatsAppTemplates();
  }
};

/**
 * Update WhatsApp message templates
 * Item 5: Custom WhatsApp templates
 */
export const updateWhatsAppTemplates = async (templates, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, {
      whatsappTemplates: templates
    }, { merge: true });
    console.log('WhatsApp templates updated');
  } catch (error) {
    console.error('Update WhatsApp templates error:', error);
    throw error;
  }
};

/**
 * Get WhatsApp template for a specific homework type
 */
export const getWhatsAppTemplateForHomework = async (homeworkType, orgId) => {
  try {
    const templates = await getWhatsAppTemplates(orgId);
    return templates[homeworkType] || templates.TH1;
  } catch (error) {
    console.error('Get WhatsApp template for homework error:', error);
    settingsReadFailures++;
    return getDefaultWhatsAppTemplates().TH1;
  }
};

/**
 * Replace template variables with actual values
 * Item 5: Template variable substitution
 *
 * Available variables:
 * {{studentName}}, {{sectionBreakdown}}, {{sectionA}}, {{sectionB}}, {{sectionC}},
 * {{total}}, {{maxMark}}, {{percentage}}, {{technical}}, {{professional}}, {{finalComment}},
 * {{summary}} (AI-generated overall summary, if enabled - see getAiSummaryEnabled),
 * {{homeworkType}}, {{homeworkName}},
 * {{technicalBreakdown}}, {{professionalBreakdown}},
 * {{technical_A}}, {{technical_B}} (per-section technical, any section ID),
 * {{professional_A}}, {{professional_B}} (per-section professional, any section ID),
 * {{professionalGroup1}}, {{professionalGroup2}} (per professional mark group),
 * {{technicalGroup1}}, {{technicalGroup2}} (per technical mark group)
 */
export const fillWhatsAppTemplate = (template, data) => {
  let message = template;

  // {{sectionBreakdown}} — total per section
  let sectionBreakdown;
  if (data.sections && data.sections.length > 0 && data.sectionScores) {
    sectionBreakdown = data.sections
      .map(s => `${s.name}: ${(data.sectionScores[s.id] ?? 0).toFixed(1)}`)
      .join('\n');
  } else {
    const lines = [];
    if (data.sectionA !== undefined) lines.push(`Section A: ${data.sectionA?.toFixed(1) || '0.0'}`);
    if (data.sectionB !== undefined) lines.push(`Section B: ${data.sectionB?.toFixed(1) || '0.0'}`);
    if (data.sectionC !== undefined) lines.push(`Section C: ${data.sectionC?.toFixed(1) || '0.0'}`);
    sectionBreakdown = lines.join('\n');
  }
  message = message.replace(/\{\{sectionBreakdown\}\}/g, sectionBreakdown);

  // {{technicalBreakdown}} — technical marks per section
  if (data.sections && data.technicalScores) {
    const techBreakdown = data.sections
      .map(s => `${s.name}: ${(data.technicalScores[s.id] ?? 0).toFixed(1)}`)
      .join('\n');
    message = message.replace(/\{\{technicalBreakdown\}\}/g, techBreakdown);
  } else {
    message = message.replace(/\{\{technicalBreakdown\}\}/g, '');
  }

  // {{professionalBreakdown}} — professional marks per group (if groups exist) or per section
  if (data.professionalGroups && data.professionalGroups.length > 0) {
    const profBreakdown = data.professionalGroups
      .map((g, i) => `${g.name || `Group ${i + 1}`}: ${Number(g.score).toFixed(1)}`)
      .join('\n');
    message = message.replace(/\{\{professionalBreakdown\}\}/g, profBreakdown);
  } else if (data.sections && data.professionalScores) {
    const profBreakdown = data.sections
      .map(s => `${s.name}: ${(data.professionalScores[s.id] ?? 0).toFixed(1)}`)
      .join('\n');
    message = message.replace(/\{\{professionalBreakdown\}\}/g, profBreakdown);
  } else {
    message = message.replace(/\{\{professionalBreakdown\}\}/g, '');
  }

  // Replace all template variables
  message = message.replace(/\{\{studentName\}\}/g, data.studentName || '');

  // Dynamic {{sectionX}} replacement for any section ID
  if (data.sectionScores) {
    Object.entries(data.sectionScores).forEach(([id, score]) => {
      message = message.replace(new RegExp(`\\{\\{section${id}\\}\\}`, 'g'), (score ?? 0).toFixed(1));
    });
  }
  // Legacy fallback for explicitly passed sectionA/B/C
  message = message.replace(/\{\{sectionA\}\}/g, data.sectionA?.toFixed(1) || '0.0');
  message = message.replace(/\{\{sectionB\}\}/g, data.sectionB?.toFixed(1) || '0.0');
  message = message.replace(/\{\{sectionC\}\}/g, data.sectionC?.toFixed(1) || '0.0');

  // Build position-based letter aliases: first section → A, second → B, third → C, etc.
  // This allows {{technical_A}} to mean "first section" regardless of actual section ID.
  const positionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];
  const positionTechnical = {};
  const positionProfessional = {};
  if (data.sections && data.sections.length > 0) {
    data.sections.forEach((s, i) => {
      const letter = positionLetters[i];
      if (letter) {
        positionTechnical[letter] = data.technicalScores?.[s.id] ?? 0;
        positionProfessional[letter] = data.professionalScores?.[s.id] ?? 0;
      }
    });
  }

  // Dynamic {{technical_X}} — match by actual section ID first, then position letter, then stay literal
  message = message.replace(/\{\{technical_([A-Za-z0-9]+)\}\}/g, (match, id) => {
    const byId = data.technicalScores?.[id] ?? data[`technical${id}`];
    if (byId != null) return Number(byId).toFixed(1);
    const byPos = positionTechnical[id];
    if (byPos != null) return Number(byPos).toFixed(1);
    return match;
  });

  // Dynamic {{professional_X}} — match by actual section ID first, then position letter, then stay literal
  message = message.replace(/\{\{professional_([A-Za-z0-9]+)\}\}/g, (match, id) => {
    const byId = data.professionalScores?.[id] ?? data[`professional${id}`];
    if (byId != null) return Number(byId).toFixed(1);
    const byPos = positionProfessional[id];
    if (byPos != null) return Number(byPos).toFixed(1);
    return match;
  });

  // {{professionalGroup1}}, {{professionalGroup2}}, etc.
  if (data.professionalGroups) {
    data.professionalGroups.forEach((g, i) => {
      message = message.replace(
        new RegExp(`\\{\\{professionalGroup${i + 1}\\}\\}`, 'g'),
        Number(g.score).toFixed(1)
      );
    });
  }

  // {{technicalGroup1}}, {{technicalGroup2}}, etc.
  if (data.technicalGroups) {
    data.technicalGroups.forEach((g, i) => {
      message = message.replace(
        new RegExp(`\\{\\{technicalGroup${i + 1}\\}\\}`, 'g'),
        Number(g.score).toFixed(1)
      );
    });
  }

  message = message.replace(/\{\{total\}\}/g, data.total?.toFixed(1) || '0.0');
  message = message.replace(/\{\{maxMark\}\}/g, data.maxMark || '20');
  message = message.replace(/\{\{percentage\}\}/g, data.percentage != null ? `${data.percentage}%` : '0%');
  message = message.replace(/\{\{technical\}\}/g, data.technical?.toFixed(1) || '0.0');
  message = message.replace(/\{\{professional\}\}/g, data.professional?.toFixed(1) || '0.0');
  message = message.replace(/\{\{finalComment\}\}/g, data.finalComment || '');
  message = message.replace(/\{\{summary\}\}/g, data.summary || '');
  message = message.replace(/\{\{homeworkType\}\}/g, data.homeworkType || '');
  message = message.replace(/\{\{homeworkName\}\}/g, data.homeworkName || '');

  return message;
};

/**
 * Default deadline reminder settings - both before/after reminders start
 * disabled so nothing sends until a head marker opts in from Settings ->
 * Reminders and configures the wording themselves.
 */
export const getDefaultReminderSettings = () => ({
  beforeEnabled: false,
  beforeDaysOffset: 3,
  beforeTemplate: `Hi {{studentName}},

Just a reminder that {{homeworkName}} is due on {{deadlineDate}}.

Please make sure to submit it in time.`,
  afterEnabled: false,
  afterDaysOffset: 1,
  afterTemplate: `Hi {{studentName}},

{{homeworkName}} was due on {{deadlineDate}} and we haven't received your submission yet.

Please submit it as soon as possible.`
});

/**
 * Get deadline reminder settings (before/after wording and day offsets).
 * One set per org, not per homework type - mirrors getWhatsAppTemplates'
 * storage location (orgs/{orgId}/settings/marking) but not its per-type shape.
 */
export const getReminderSettings = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return getDefaultReminderSettings();
    }
    return { ...getDefaultReminderSettings(), ...(docSnap.data().reminderSettings || {}) };
  } catch (error) {
    console.error('Get reminder settings error:', error);
    settingsReadFailures++;
    return getDefaultReminderSettings();
  }
};

/**
 * Update deadline reminder settings
 */
export const updateReminderSettings = async (settings, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { reminderSettings: settings }, { merge: true });
    console.log('Reminder settings updated');
  } catch (error) {
    console.error('Update reminder settings error:', error);
    throw error;
  }
};

/**
 * Fill a reminder email template's {{studentName}}, {{homeworkName}},
 * {{deadlineDate}} placeholders - deliberately simpler than
 * fillWhatsAppTemplate above since reminders don't carry marks/scores.
 */
export const fillReminderTemplate = (template, data) => {
  let message = template;
  message = message.replace(/\{\{studentName\}\}/g, data.studentName || '');
  message = message.replace(/\{\{homeworkName\}\}/g, data.homeworkName || '');
  message = message.replace(/\{\{deadlineDate\}\}/g, data.deadlineDate || '');
  return message;
};

/**
 * Get default section structure
 * Item 10: Section structure configuration
 */
export const getDefaultSectionStructure = () => {
  return {
    TH1: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    },
    TH2: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    },
    TH3: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    },
    TH4: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    },
    RH1: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    },
    RH2: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    },
    RH3: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    },
    RH4: {
      sections: [
        { id: 'A', name: 'Section A', maxMark: 6 },
        { id: 'B', name: 'Section B', maxMark: 7 },
        { id: 'C', name: 'Section C', maxMark: 7 }
      ]
    }
  };
};

/**
 * Get section structures for all homework types
 * Item 10: Section structure configuration
 */
export const getSectionStructures = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return getDefaultSectionStructure();
    }

    return docSnap.data().sectionStructures || getDefaultSectionStructure();
  } catch (error) {
    console.error('Get section structures error:', error);
    settingsReadFailures++;
    return getDefaultSectionStructure();
  }
};

/**
 * Update section structures
 * Item 10: Section structure configuration
 */
export const updateSectionStructures = async (structures, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, {
      sectionStructures: structures
    }, { merge: true });
    console.log('Section structures updated');
  } catch (error) {
    console.error('Update section structures error:', error);
    throw error;
  }
};

/**
 * Get section structure for a specific homework type
 * Item 10: Section structure for specific homework
 */
export const getSectionStructureForHomework = async (homeworkType, orgId) => {
  try {
    const structures = await getSectionStructures(orgId);
    return structures[homeworkType] || getDefaultSectionStructure()[homeworkType];
  } catch (error) {
    console.error('Get section structure for homework error:', error);
    settingsReadFailures++;
    return getDefaultSectionStructure()[homeworkType];
  }
};

// Alias for backwards compatibility
export const getDefaultSectionStructures = getDefaultSectionStructure;

/**
 * Get whether email verification is required for new student registrations
 */
export const getEmailVerificationRequired = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return false;
    return docSnap.data().requireEmailVerification === true;
  } catch (error) {
    console.error('Get email verification setting error:', error);
    settingsReadFailures++;
    return false;
  }
};

/**
 * Update whether email verification is required for new student registrations
 */
export const updateEmailVerificationRequired = async (required, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { requireEmailVerification: required }, { merge: true });
  } catch (error) {
    console.error('Update email verification setting error:', error);
    throw error;
  }
};

/**
 * Get whether AI-generated overall summaries are enabled for this org.
 * Off by default - opt-in, generated in the background by the
 * onSubmissionAwaitingReview Cloud Function once turned on.
 */
export const getAiSummaryEnabled = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return false;
    return docSnap.data().aiSummaryEnabled === true;
  } catch (error) {
    console.error('Get AI summary setting error:', error);
    settingsReadFailures++;
    return false;
  }
};

/**
 * Update whether AI-generated overall summaries are enabled for this org
 */
export const updateAiSummaryEnabled = async (enabled, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { aiSummaryEnabled: enabled }, { merge: true });
  } catch (error) {
    console.error('Update AI summary setting error:', error);
    throw error;
  }
};

/**
 * Tutor approval: which new students must be approved by a head marker
 * before they can see this org's homework - 'all' (new registrations and
 * students adding this tutor), 'new' (new registrations only), 'added'
 * (students adding this tutor only) or 'none' (default).
 */
export const STUDENT_APPROVAL_MODES = ['all', 'new', 'added', 'none'];

export const getStudentApprovalMode = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    const mode = docSnap.exists() ? docSnap.data().studentApprovalMode : null;
    return STUDENT_APPROVAL_MODES.includes(mode) ? mode : 'none';
  } catch (error) {
    console.error('Get student approval setting error:', error);
    settingsReadFailures++;
    return 'none';
  }
};

export const updateStudentApprovalMode = async (mode, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { studentApprovalMode: STUDENT_APPROVAL_MODES.includes(mode) ? mode : 'none' }, { merge: true });
  } catch (error) {
    console.error('Update student approval setting error:', error);
    throw error;
  }
};

/**
 * Whether this org emails its markers when a homework is submitted (the
 * onSubmissionCreated Cloud Function), to the markers who opted in per
 * homework type under orgs/{orgId}/notificationSettings. Off unless set.
 */
export const getMarkerSubmissionEmailsEnabled = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return false;
    return docSnap.data().markerSubmissionEmails === true;
  } catch (error) {
    console.error('Get marker submission emails setting error:', error);
    settingsReadFailures++;
    return false;
  }
};

export const updateMarkerSubmissionEmailsEnabled = async (enabled, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { markerSubmissionEmails: enabled }, { merge: true });
  } catch (error) {
    console.error('Update marker submission emails setting error:', error);
    throw error;
  }
};

export const getQuestionStructures = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().questionStructures || {};
  } catch (error) {
    console.error('Get question structures error:', error);
    settingsReadFailures++;
    return {};
  }
};

export const updateQuestionStructures = async (structures, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { questionStructures: structures }, { merge: true });
  } catch (error) {
    console.error('Update question structures error:', error);
    throw error;
  }
};

/**
 * Get the webhook URL fired automatically on every new student submission
 * (a lightweight per-submission ping, separate from the manual Sync tab
 * export - see getStudentDataWebhookUrl below). Empty by default for an
 * org until a head marker configures one.
 */
export const getSubmissionWebhookUrl = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return '';
    return docSnap.data().submissionWebhookUrl || '';
  } catch (error) {
    console.error('Get submission webhook URL error:', error);
    settingsReadFailures++;
    return '';
  }
};

/**
 * Update the per-submission webhook URL
 */
export const updateSubmissionWebhookUrl = async (url, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { submissionWebhookUrl: url }, { merge: true });
  } catch (error) {
    console.error('Update submission webhook URL error:', error);
    throw error;
  }
};

/**
 * Get whether/how this org allows the platform admin (Stuart) to enter this
 * org's dashboard for support (see functions/index.js's
 * mintSupportSessionToken). Defaults to 'always' when nothing has been set
 * yet - matches the server-side fallback and what onboardNewOrg seeds for a
 * new tutor - so a new org isn't silently locked out before she's even
 * looked at this setting.
 */
export const getSupportAccess = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists() || !docSnap.data().supportAccess) {
      return { mode: 'always', expiresAt: null };
    }
    return docSnap.data().supportAccess;
  } catch (error) {
    console.error('Get support access error:', error);
    settingsReadFailures++;
    return { mode: 'always', expiresAt: null };
  }
};

/**
 * Update this org's support access mode. `expiresAt` should be a JS Date
 * (or null for 'off'/'always').
 */
export const updateSupportAccess = async ({ mode, expiresAt }, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { supportAccess: { mode, expiresAt: expiresAt || null } }, { merge: true });
  } catch (error) {
    console.error('Update support access error:', error);
    throw error;
  }
};

/**
 * Get the Student Data Sheet webhook URL used by the Sync tab.
 * Stored in Firestore (not localStorage) so it is shared across devices/browsers
 * for anyone logged in as head marker.
 */
export const getStudentDataWebhookUrl = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return '';
    return docSnap.data().studentDataWebhookUrl || '';
  } catch (error) {
    console.error('Get student data webhook URL error:', error);
    settingsReadFailures++;
    return '';
  }
};

/**
 * Update the Student Data Sheet webhook URL used by the Sync tab
 */
export const updateStudentDataWebhookUrl = async (url, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { studentDataWebhookUrl: url }, { merge: true });
  } catch (error) {
    console.error('Update student data webhook URL error:', error);
    throw error;
  }
};

/**
 * Get the single per-org Tax Tables / Help document used by every exam-mode
 * homework's Help button. Returns null if none has been uploaded yet.
 */
export const getExamHelpDocument = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return docSnap.data().examHelpDocument || null;
  } catch (error) {
    console.error('Get exam help document error:', error);
    settingsReadFailures++;
    return null;
  }
};

/**
 * Update (or clear, passing null) the per-org Tax Tables / Help document
 */
export const updateExamHelpDocument = async (document, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { examHelpDocument: document }, { merge: true });
  } catch (error) {
    console.error('Update exam help document error:', error);
    throw error;
  }
};

/**
 * Whether the Help / Tax Tables button shows in exam mode. Stored inverted
 * (examHelpButtonHidden) so orgs that have never set it keep the button.
 */
export const getExamHelpButtonVisible = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return true;
    return docSnap.data().examHelpButtonHidden !== true;
  } catch (error) {
    console.error('Get exam help button visibility error:', error);
    settingsReadFailures++;
    return true;
  }
};

export const updateExamHelpButtonVisible = async (visible, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { examHelpButtonHidden: !visible }, { merge: true });
  } catch (error) {
    console.error('Update exam help button visibility error:', error);
    throw error;
  }
};

/**
 * Get the per-org default submission mode ('pdf' | 'exam') per homework type.
 * Individual cohorts may override this - see getEffectiveSubmissionMode in cohorts.js.
 */
export const getSubmissionModes = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().submissionModes || {};
  } catch (error) {
    console.error('Get submission modes error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update the per-org default submission mode per homework type
 */
export const updateSubmissionModes = async (modes, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { submissionModes: modes }, { merge: true });
  } catch (error) {
    console.error('Update submission modes error:', error);
    throw error;
  }
};

/**
 * Get exam duration (in minutes) per homework type, for exam-mode homeworks
 */
export const getExamDurations = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().examDurations || {};
  } catch (error) {
    console.error('Get exam durations error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update exam durations (in minutes) per homework type
 */
export const updateExamDurations = async (durations, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { examDurations: durations }, { merge: true });
  } catch (error) {
    console.error('Update exam durations error:', error);
    throw error;
  }
};

/**
 * Get which homework types have no time limit at all, for exam-mode
 * homeworks - a separate boolean map rather than a sentinel value in
 * examDurations, since examDurations already treats any falsy value as
 * "use the default duration".
 */
export const getExamUnlimitedDurations = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().examUnlimitedDurations || {};
  } catch (error) {
    console.error('Get exam unlimited durations error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update which homework types have no time limit
 */
export const updateExamUnlimitedDurations = async (unlimitedDurations, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { examUnlimitedDurations: unlimitedDurations }, { merge: true });
  } catch (error) {
    console.error('Update exam unlimited durations error:', error);
    throw error;
  }
};

/**
 * Get which homework types show the standalone "what's expected" breakdown
 * screen between Task Summary and the split-screen workspace, for exam-mode
 * homeworks. Off by default - the breakdown text is always shown inside the
 * split screen's "QUESTION INFORMATION" pane regardless of this setting;
 * this only controls the extra standalone stop before it.
 */
export const getExamShowBreakdownScreen = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().examShowBreakdownScreen || {};
  } catch (error) {
    console.error('Get exam show breakdown screen error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update which homework types show the standalone breakdown screen
 */
export const updateExamShowBreakdownScreen = async (showBreakdownScreen, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { examShowBreakdownScreen: showBreakdownScreen }, { merge: true });
  } catch (error) {
    console.error('Update exam show breakdown screen error:', error);
    throw error;
  }
};

/**
 * Get which homework types capture the exam spreadsheet's structured cell
 * data (values + formulas) at submission time, letting the marker view/click
 * cells interactively instead of only reading the flattened PDF. Off by
 * default - a homework type with this off behaves exactly as before, with no
 * extra data captured and no change to the marker's screen.
 */
export const getExamInteractiveSpreadsheetMarking = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().examInteractiveSpreadsheetMarking || {};
  } catch (error) {
    console.error('Get exam interactive spreadsheet marking error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update which homework types capture interactive spreadsheet marking data
 */
export const updateExamInteractiveSpreadsheetMarking = async (interactiveSpreadsheetMarking, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { examInteractiveSpreadsheetMarking: interactiveSpreadsheetMarking }, { merge: true });
  } catch (error) {
    console.error('Update exam interactive spreadsheet marking error:', error);
    throw error;
  }
};

/**
 * Get which homework types offer an optional spreadsheet attachment
 * alongside the normal PDF Upload flow (see StudentDashboard.jsx) - lets a
 * student submit a PDF, a spreadsheet, or both together in one submission.
 * Only relevant when that type's Submission Mode is 'pdf'. Off by default -
 * a homework type with this off shows only the plain PDF dropzone, exactly
 * as before this existed.
 */
export const getPdfSpreadsheetAttachmentEnabled = async (orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    return docSnap.data().pdfSpreadsheetAttachmentEnabled || {};
  } catch (error) {
    console.error('Get PDF spreadsheet attachment enabled error:', error);
    settingsReadFailures++;
    return {};
  }
};

/**
 * Update which homework types offer the optional spreadsheet attachment
 */
export const updatePdfSpreadsheetAttachmentEnabled = async (pdfSpreadsheetAttachmentEnabled, orgId) => {
  try {
    const docRef = await getMarkingSettingsRef(orgId);
    await setDoc(docRef, { pdfSpreadsheetAttachmentEnabled }, { merge: true });
  } catch (error) {
    console.error('Update PDF spreadsheet attachment enabled error:', error);
    throw error;
  }
};
